import {
  Container,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import {
  useTaskAreaAddStores,
  useTaskAreaRemoveStores
} from "@saleor/dropOff/mutation";
import { useGetTenantsQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import {
  GetTemplate,
  GetTemplate_template_taskAreas_edges,
  GetTemplateVariables
} from "@saleor/dropOff/types/GetTemplate";
import { GetTenants_tenants_edges_node_taskAreas_edges } from "@saleor/dropOff/types/GetTenants";
import { TemplateAction } from "@saleor/dropOff/utils/handlers";
import useListSettings from "@saleor/hooks/useListSettings";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import { maybe } from "@saleor/misc";
import { ListSettings, ListViews, Pagination } from "@saleor/types";
import {
  LogisticsStatusEnum,
  SmopStatusEnum,
  StoreTypeEnum
} from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import { ApolloQueryResult } from "apollo-client";
import * as React from "react";

import DropOffTaskAreaAddRemoveButton from "../DropOffTaskAreaAddRemoveButton";
import DropOffTaskAreaAssignedStores from "../DropOffTaskAreaAssignedStores";
import DropOffTaskAreaAvailableStores from "../DropOffTaskAreaAvailableStores";

interface DropOffAssignStoresProps {
  mallId: string;
  templateId: string;
  selectedTaskArea: any;
  searchAssignedStores: string;
  setSearchAssignedStores: React.Dispatch<React.SetStateAction<string>>;
  setFilterAssignedStores: React.Dispatch<React.SetStateAction<StoreTypeEnum>>;
  templateTaskAreas: GetTemplate_template_taskAreas_edges[];
  refetchTemplate: (
    variables?: GetTemplateVariables
  ) => Promise<ApolloQueryResult<GetTemplate>>;
  setTemplateParams: (value: any) => void;

  templateLoading: boolean;
  templateSettings: ListSettings<string>;
  templateUpdateListSettings: (
    key: keyof ListSettings<any>,
    value: any
  ) => void;
  templatePageInfo: any;
  onTemplateLoadNextPage: () => void;
  onTemplateLoadPreviousPage: () => void;
  onClose: () => void;

  isSelectAllAvailableStores: boolean;
  setIsSelectAllAvailableStores: React.Dispatch<React.SetStateAction<boolean>>;
  isSelectAllAssignedStores: boolean;
  setIsSelectAllAssignedStores: React.Dispatch<React.SetStateAction<boolean>>;
  templateAction: TemplateAction;
}

const DropOffAssignStores: React.FC<DropOffAssignStoresProps> = props => {
  const {
    mallId,
    templateId,
    selectedTaskArea,
    templateTaskAreas,
    searchAssignedStores,
    setSearchAssignedStores,
    setFilterAssignedStores,
    refetchTemplate,
    setTemplateParams,
    templateLoading,
    templateSettings,
    templateUpdateListSettings,
    templatePageInfo,
    onTemplateLoadNextPage,
    onTemplateLoadPreviousPage,
    onClose,
    isSelectAllAvailableStores,
    setIsSelectAllAvailableStores,
    isSelectAllAssignedStores,
    setIsSelectAllAssignedStores,
    templateAction
  } = props;

  const classes = useTemplateStyles(props);
  const notify = useNotifier();
  const paginate = usePaginator();

  const [availableStores, setAvailableStores] = React.useState([]);
  const [assignedStores, setAssignedStores] = React.useState([]);

  const [
    disableSelectAllAssignedStores,
    setDisableSelectAllAssignedStores
  ] = React.useState<boolean>(false);
  const [disableAssignButton, setDisableAssignButton] = React.useState(false);
  const [disableRemoveButton, setDisableRemoveButton] = React.useState(false);

  const [searchAvailableStores, setSearchAvailableStores] = React.useState("");
  const [filterAvailableStores, setFilterAvailableStores] = React.useState<
    StoreTypeEnum
  >(null);

  const [params, setParams] = React.useState<Pagination>({
    after: null,
    before: null
  });

  React.useMemo(() => {
    if (!templateTaskAreas) {
      return;
    }
    const activeTaskAreas = [];

    for (const ta of templateTaskAreas) {
      if (ta.node.status !== LogisticsStatusEnum.DELETED) {
        const listTenants = [];
        ta.node.tenants.edges.forEach(tenant => {
          listTenants.push({
            id: tenant.node.id,
            isChecked: false,
            status: tenant.node.status,
            storeName: tenant.node.storeName
          });
        });

        let isExpanded = false;
        if (ta.node.id === selectedTaskArea?.id) {
          isExpanded = true;
        }

        activeTaskAreas.push({
          id: ta.node.id,
          isExpanded,
          name: ta.node.name,
          status: ta.node.status,
          taskAreas: ta.node.tenants.edges,
          tenants: listTenants
        });
      }
    }

    setAssignedStores(activeTaskAreas);
  }, [templateTaskAreas]);

  const clickSelectAllAvailableStores = () => {
    const updatedValue = !isSelectAllAvailableStores;
    setIsSelectAllAvailableStores(updatedValue);

    const selectedList = [];
    availableStores.forEach(tenant => {
      tenant.isChecked = updatedValue;
      selectedList.push(tenant);
    });

    setAvailableStores(selectedList);
  };

  React.useMemo(() => {
    const selectedList = [];
    if (isSelectAllAvailableStores) {
      availableStores.forEach(tenant => {
        selectedList.push(tenant);
      });
      setAvailableStores(selectedList);
    }
  }, [isSelectAllAvailableStores]);

  const { updateListSettings, settings } = useListSettings(
    ListViews.DISPATCH_LIST
  );

  const checkDisable = (
    taskAreas: GetTenants_tenants_edges_node_taskAreas_edges[]
  ) => {
    const taId = selectedTaskArea?.id;
    if (taId && taskAreas) {
      const taskAreaTenant = [];
      taskAreas.forEach(taskArea => {
        if (taskArea?.node?.id) {
          taskAreaTenant.push(taskArea?.node?.id);
        }
      });
      return taskAreaTenant.includes(taId.toString());
    }
    return false;
  };

  const {
    data: tenants,
    refetch: refetchTenants,
    loading: tenantsLoading
  } = useGetTenantsQuery({
    displayLoader: true,
    variables: {
      after: params?.after ? params.after : null,
      before: params?.before ? params.before : null,
      first: params?.before ? null : settings.rowNumber,
      isNotAssignedInTemplates: [templateId],
      last: params?.before ? settings.rowNumber : null,
      mallId,
      search: searchAvailableStores,
      status: SmopStatusEnum.ACT,
      storeType: filterAvailableStores
    }
  });

  React.useMemo(() => {
    if (tenants && tenants?.tenants?.totalCount >= 0) {
      const filteredList = tenants?.tenants?.edges.map(t => t.node);
      const activeList = filteredList.map(tenant => ({
        isChecked: checkDisable(tenant.taskAreas.edges),
        status: tenant.status,
        storeName: tenant.storeName,
        taskAreas: tenant.taskAreas.edges,
        tenantId: tenant.id
      }));
      setAvailableStores(activeList);
    }
  }, [tenants]);

  const resetSelection = () => {
    if (tenants && tenants?.tenants?.totalCount >= 0) {
      const filteredList = tenants?.tenants?.edges.map(t => t.node);
      const activeList = filteredList.map(tenant => {
        if (tenant.status === "ACT") {
          return {
            isChecked: checkDisable(tenant.taskAreas.edges),
            status: tenant.status,
            storeName: tenant.storeName,
            taskAreas: tenant.taskAreas.edges,
            tenantId: tenant.id
          };
        }
      });

      setAvailableStores(activeList);
    }
    setParams({
      after: null,
      before: null
    });
    setTemplateParams({
      after: null,
      before: null
    });
    setIsSelectAllAvailableStores(false);
    setSearchAvailableStores("");
    setIsSelectAllAssignedStores(false);
    setSearchAssignedStores("");
  };

  const clickSelectAllAssignedStores = () => {
    if (assignedStores) {
      const updatedValue = !isSelectAllAssignedStores;
      setIsSelectAllAssignedStores(updatedValue);

      const selectedList = [];
      assignedStores.forEach(ta => {
        if (ta?.tenants) {
          ta.tenants.forEach(tenant => {
            tenant.isChecked = updatedValue;
          });
        }
        selectedList.push(ta);
      });

      setAssignedStores(selectedList);
    }
  };

  React.useEffect(() => {
    const hasSelected = availableStores.some(tenant => tenant.isChecked);
    setDisableAssignButton(!hasSelected);
  }, [availableStores]);

  React.useEffect(() => {
    const activeListCopy = availableStores.map(tenant => ({
      isChecked: checkDisable(tenant.taskAreas),
      status: tenant.status,
      storeName: tenant.storeName,
      taskAreas: tenant.taskAreas,
      tenantId: tenant.tenantId
    }));
    setAvailableStores(activeListCopy);

    const taskArea = assignedStores.find(ta => ta.isExpanded);
    const hasSelected =
      taskArea?.tenants?.some(tenant => tenant.isChecked) || false;
    setDisableRemoveButton(!hasSelected);

    let selectedHasTenants = true;
    if (selectedTaskArea?.id) {
      const taskAreaSelected = assignedStores.find(
        ta => ta.isExpanded && ta.id === selectedTaskArea?.id
      );
      if (taskAreaSelected?.tenants && taskAreaSelected?.tenants?.length > 0) {
        selectedHasTenants = false;
      }
    }
    setDisableSelectAllAssignedStores(selectedHasTenants);
  }, [assignedStores]);

  // ADD & REMOVE TENANT
  const [addStoresTaskArea] = useTaskAreaAddStores({
    onCompleted: e => {
      if (e.taskAreaAddStores.taskAreaError.length === 0) {
        notify({
          status: "success",
          text: "Stores Added."
        });
        resetSelection();
        refetchTemplate();
        refetchTenants();
      } else {
        notify({
          status: "error",
          text: e.taskAreaAddStores.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const onclickAssignTenant = () => {
    // Find selected tenants
    const selectedTenantIds = [];
    availableStores.forEach(tenant => {
      if (tenant.isChecked) {
        selectedTenantIds.push(tenant.tenantId);
      }
    });

    if (selectedTenantIds.length === 0) {
      notify({
        status: "error",
        text: "Select at least one store."
      });
      return;
    }

    if (selectedTaskArea?.id) {
      // Update TA Stores
      addStoresTaskArea({
        variables: {
          input: {
            id: selectedTaskArea?.id,
            tenants: selectedTenantIds
          }
        }
      });
    } else {
      notify({
        status: "error",
        text: "Please select a Task Area."
      });
    }
  };

  const [removeStoresTaskArea] = useTaskAreaRemoveStores({
    onCompleted: e => {
      if (e.taskAreaRemoveStores.taskAreaError.length === 0) {
        notify({
          status: "success",
          text: "Stores Removed."
        });
        resetSelection();
        refetchTemplate();
        refetchTenants();
      } else {
        notify({
          status: "error",
          text: e.taskAreaRemoveStores.taskAreaError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const onclickRemoveTenant = () => {
    const taskArea = assignedStores.find(ta => ta.isExpanded);

    if (typeof taskArea === "undefined") {
      notify({
        status: "error",
        text: "Please select a store to remove."
      });
      return;
    }

    const selectedTenantIds = [];
    taskArea.tenants.map(tenant => {
      if (tenant.isChecked) {
        selectedTenantIds.push(tenant.id);
      }
    });

    if (selectedTenantIds.length === 0) {
      notify({
        status: "error",
        text: "Please select a store to remove."
      });
      return;
    }

    removeStoresTaskArea({
      variables: {
        input: {
          id: taskArea.id,
          tenants: selectedTenantIds
        }
      }
    });

    if (isSelectAllAssignedStores) {
      setIsSelectAllAssignedStores(false);
    }
  };

  // AVAILABLE STORES
  const onSearchChangeAvailableStores = (text: string) => {
    setParams({
      after: null,
      before: null
    });
    setSearchAvailableStores(text);
    if (isSelectAllAvailableStores) {
      setIsSelectAllAvailableStores(false);
    }
    refetchTenants();
  };

  const onFilterChangeAvailableStores = (index: number) => {
    let opt = null;
    if (index === 1) {
      opt = StoreTypeEnum.FOOD_AND_BEVERAGES;
    } else if (index === 2) {
      opt = StoreTypeEnum.GENERAL_MERCHANDISE;
    }
    setFilterAvailableStores(opt);
    setParams({
      after: null,
      before: null
    });
    if (isSelectAllAvailableStores) {
      setIsSelectAllAvailableStores(false);
    }
    refetchTenants();
  };

  React.useEffect(() => {
    setParams({
      after: null,
      before: null
    });
    if (isSelectAllAvailableStores) {
      setIsSelectAllAvailableStores(false);
    }
  }, [settings.rowNumber]);

  const paginationState = createPaginationState(settings.rowNumber, params);

  const { pageInfo } = paginate(
    maybe(() => tenants.tenants.pageInfo),
    paginationState,
    params
  );

  const onLoadNextPage = () => {
    setParams({
      after: pageInfo.endCursor,
      before: null
    });
    if (isSelectAllAvailableStores) {
      setIsSelectAllAvailableStores(false);
    }
    refetchTenants();
  };

  const onLoadPreviousPage = () => {
    setParams({
      after: null,
      before: pageInfo.startCursor
    });
    if (isSelectAllAvailableStores) {
      setIsSelectAllAvailableStores(false);
    }
    refetchTenants();
  };

  // ASSIGNED STORES
  const onSearchChangeAssignedStores = (text: string) => {
    setTemplateParams({
      after: null,
      before: null
    });
    setSearchAssignedStores(text);
    if (isSelectAllAssignedStores) {
      setIsSelectAllAssignedStores(false);
    }
    refetchTemplate();
  };

  const onFilterChangeAssignedStores = (index: number) => {
    let opt = null;
    if (index === 1) {
      opt = StoreTypeEnum.FOOD_AND_BEVERAGES;
    } else if (index === 2) {
      opt = StoreTypeEnum.GENERAL_MERCHANDISE;
    }
    setFilterAssignedStores(opt);
    setTemplateParams({
      after: null,
      before: null
    });
    if (isSelectAllAssignedStores) {
      setIsSelectAllAssignedStores(false);
    }
    refetchTemplate();
  };

  React.useEffect(() => {
    setTemplateParams({
      after: null,
      before: null
    });
    if (isSelectAllAssignedStores) {
      setIsSelectAllAssignedStores(false);
    }
  }, [templateSettings.rowNumber]);

  return (
    <Container>
      <DialogTitle disableTypography style={{ padding: "7px 0px" }}>
        <Typography className={classes.taskAreaName} variant="h5">
          {selectedTaskArea?.name}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={() => onClose()}
          style={{
            padding: "0",
            right: "55px",
            top: "30px"
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <div className={classes.taskAreaCardDiv}>
        <Grid
          {...(templateAction === TemplateAction.VIEW
            ? {}
            : { container: true, spacing: 1 })}
        >
          {templateAction === TemplateAction.VIEW ? (
            <></>
          ) : (
            <>
              <DropOffTaskAreaAvailableStores
                availableStores={availableStores}
                setAvailableStores={setAvailableStores}
                tenantsLoading={tenantsLoading}
                searchAvailableStores={searchAvailableStores}
                onSearchChangeAvailableStores={onSearchChangeAvailableStores}
                onFilterChangeAvailableStores={onFilterChangeAvailableStores}
                isSelectAllAvailableStores={isSelectAllAvailableStores}
                clickSelectAllAvailableStores={clickSelectAllAvailableStores}
                pageInfo={pageInfo}
                settings={settings}
                updateListSettings={updateListSettings}
                onLoadNextPage={onLoadNextPage}
                onLoadPreviousPage={onLoadPreviousPage}
              />

              <DropOffTaskAreaAddRemoveButton
                disableAssignButton={disableAssignButton}
                disableRemoveButton={disableRemoveButton}
                onclickAssignTenant={onclickAssignTenant}
                onclickRemoveTenant={onclickRemoveTenant}
              />
            </>
          )}

          <DropOffTaskAreaAssignedStores
            assignedStores={assignedStores}
            setAssignedStores={setAssignedStores}
            searchAssignedStores={searchAssignedStores}
            onSearchChangeAssignedStores={onSearchChangeAssignedStores}
            onFilterChangeAssignedStores={onFilterChangeAssignedStores}
            selectedTaskArea={selectedTaskArea}
            isSelectAllAssignedStores={isSelectAllAssignedStores}
            disableSelectAllAssignedStores={disableSelectAllAssignedStores}
            clickSelectAllAssignedStores={clickSelectAllAssignedStores}
            templateLoading={templateLoading}
            templateSettings={templateSettings}
            templateUpdateListSettings={templateUpdateListSettings}
            templatePageInfo={templatePageInfo}
            onTemplateLoadNextPage={onTemplateLoadNextPage}
            onTemplateLoadPreviousPage={onTemplateLoadPreviousPage}
            templateAction={templateAction}
          />
        </Grid>
      </div>
    </Container>
  );
};

export default DropOffAssignStores;
